"use client"

import { useState, useEffect } from "react"
import { useParams, Link, useSearchParams , useNavigate} from "react-router-dom"
import { v4 as uuidv4 } from 'uuid';
import {
  Typography,
  Row,
  Col,
  Card,
  Button,
  InputNumber,
  Input,
  Tabs,
  Rate,
  Form,
  Select,
  Breadcrumb,
  Divider,
  notification,
  Space,
} from "antd"
import { ShoppingCartOutlined, HeartOutlined, CheckCircleOutlined, InfoCircleOutlined, MoneyCollectOutlined, CreditCardFilled } from "@ant-design/icons"
import { motion } from "framer-motion"
import axios from 'axios'
import { useCart } from "../contexts/CartContext"
import "../styles/product-cards.css"
import "./ProductDetailPage.css"
import { stringToRegex } from "../utils/utils"
import { useWallet } from "../contexts/WalletContext";
import { apiRequest } from "../utils/apiRequest";
import { useCurrency } from "../contexts/CurrencyContext";
import Seo, { siteBase } from "../components/Seo";
import { buildProductJsonLd, buildBreadcrumbJsonLd, slugifyCategory } from "../lib/seo";
import useCategoryBySlug from "../hooks/useCategoryBySlug";
import { LANDING_COPY } from "../lib/landingCopy"
import { assetUrl } from "../lib/assetUrl"

const { Title, Text, Paragraph } = Typography
const { TabPane } = Tabs
const { Option } = Select

const ProductDetailPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate()

  const {rate} = useWallet()
  const { formatMoney, formatRange } = useCurrency()

  // This page serves two URL shapes for the same component:
  //   /product/:id            → a normal catalogue product
  //   /product/:id?conv=true  → a category-as-product (legacy alias, still supported)
  //   /buy/:slug              → the SAME category-as-product, canonical form
  // On /buy/:slug the numeric id is resolved from the slug via the categories list.
  const { id: routeId, slug } = useParams()
  const { category: convCategory, loading: convLoading } = useCategoryBySlug(slug)

  const isConv = slug ? 'true' : searchParams.get('conv');
  const id = slug ? convCategory?.id : routeId
  const { addToCart } = useCart()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [account, setAccount] = useState("")
  const [notify, setNotify] = useState("")
  const [userPrice, setUserPrice] = useState(0)
  const [related, setRelated] = useState([])
  const [category, setCategory] = useState(searchParams.get('category'))
  const [selectedOptions, setSelectedOptions] = useState({})
  const [customPrice, setCustomPrice] = useState(0)
  const [form] = Form.useForm();
 
  const parseNotice = (value) => {
    if (!value) return null;
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed : [parsed]; }
      catch { return [value]; }
    }
    return null;
  };

  useEffect(() => {
    // On /buy/:slug the id is not known until the slug resolves — don't fire the
    // request with id=undefined.
    if (!id) return

    const fetchProduct = async () => {
      setLoading(true)
      let url = `/product?id=${id}&isconv=${isConv}`;

      const response = await apiRequest({
        method: "GET",
        url,
      })

      let data = response.data;
      setLoading(false)

      if(typeof data === 'object'){
        if(Object.keys(data).length < 1){
          return setProduct(null)
        }
      }

      let final = {
        id: data.id,
        ext_id: data.product_id,
        code: data.productCode,
        name: data.name,
        price: data.price,
        minPrice: data.minPrice || data.metadata.MinimumRecharge,
        maxPrice:data.maxPrice || data.metadata.MaxRecharge,
        category:data.category,
        provider:data.provider,
        imageUrl: assetUrl(data.logo),
        category_id: data.category_id,
        validation: data.validation,
        validation_message: data.validation_message,
        product_notice: parseNotice(data.product_notice),
        category_notice: parseNotice(data.category_notice),
        provider_notice: parseNotice(data.provider_notice),
        currency:data.metadata?.Currency || data.currency,
        required: data.requiredValues
      };

      setProduct(final)
      setLoading(false)
    }

    const fetchRelatedProducts = async() => {
      let i = 0;

      if(isConv)
        {
          i = id
        }else
        {
          i = category
        }

      let url = `/products`;
    
      // Create URLSearchParams for all parameters
      const params = new URLSearchParams();

      if(id){params.append('category', i)}

      params.append('limit', 4);

      url += `?${params.toString()}`;

      const response = await apiRequest({
        method: "GET",
        url,
      })

      const p = await Promise.all(
        response.data.products.map(async (product) => {
  
          return {
            id: product.id,
            code: product.productCode,
            title: product.name,
            price: product.price,
            icon: assetUrl(product.logo),
            color: 'orange',
            category_id: product.category_id
          };
        })
      );

      setRelated(p)

    }

    fetchProduct()
    fetchRelatedProducts()
  }, [id, form])

  const handleAddToCart = async () => {
    let values = await form.validateFields();
    if (!product) return

    const notices = [
      ...(product.product_notice || []),
      ...(product.category_notice || []),
      ...(product.provider_notice || []),
    ];

    const item = {
      id: uuidv4(),
      productId: product.id,
      ext_id: product.ext_id ,
      code: product.code,
      name: product.name,
      price: product.price || values.userPrice,
      imageUrl: product.imageUrl,
      category: product.category,
      account: values.account,
      isConv : isConv ? true : false,
      notifyNumber: values?.NotifyNumber,
      notices,
    }

    await addToCart(item)

    notification.success({
      message: "Added to Cart",
      description: `${product.name} has been added to your cart.`,
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
    })
  }

  const handleBuyNow= async () => {
    let values = await form.validateFields();
    if (!product) return

    const notices = [
      ...(product.product_notice || []),
      ...(product.category_notice || []),
      ...(product.provider_notice || []),
    ];

     const item = {
      id: uuidv4(),
      productId: product.id,
      ext_id: product.ext_id ,
      code: product.code,
      name: product.name,
      price: product.price || values.userPrice,
      imageUrl: product.imageUrl,
      category: product.category,
      account: values.account,
      isConv : isConv ? true : false,
      notifyNumber: values?.NotifyNumber,
      notices,
    }

    addToCart(item)

    navigate('/checkout',{})
  }

  const formatPrice = (price) => {
    if (Array.isArray(price)) return formatRange(price)
    if (price === 0) return "Variable Price"
    return formatMoney(price)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } },
  }

  if (loading || convLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
      </div>
    )
  }

  if (!product) {
    return (
      <div className="product-not-found">
        <Title level={2}>Product Not Found</Title>
        <Button type="primary">
          <Link to="/">Back to Shop</Link>
        </Button>
      </div>
    )
  }

  // Keyword-rich, network-aware SEO copy for search queries like "buy econet airtime".
  //
  // Curated copy wins where we have it. These /buy/ pages are the highest-intent
  // pages on the site (ZESA tokens, USD airtime), and until now they could not
  // use LANDING_COPY at all: the curated entries were written against
  // /products/<slug>, which redirects here, so they never rendered.
  const seoBrand = product.provider || product.category || "Tanaka Life"
  const buySlug = slug || (product.name ? slugifyCategory(product.name) : null)
  const curated = buySlug ? LANDING_COPY[buySlug] : null

  const seoTitle = curated?.title || `Buy ${product.name} Online — Instant Delivery`
  const seoDescription = curated?.intro
    || `Buy ${product.name}${product.provider ? ` (${product.provider})` : ""} online with Tanaka Life. ` +
    `Instant airtime, data bundles and ZESA tokens to Zimbabwe from South Africa and the diaspora. ` +
    `Secure payment by voucher, card or instant EFT.`
  // Canonical must distinguish a category-as-product from a catalogue product.
  // These are DIFFERENT items that happen to share an id namespace: /product/11 is an
  // Econet data bundle, while /product/11?conv=true is ZETDC (ZESA tokens). Emitting
  // /product/11 for both told Google the ZESA page's canonical was the data bundle, so
  // the ZESA page was dropped and its signals folded into the wrong item.
  // Both the /buy/:slug route and the ?conv=true alias resolve to the same /buy/<slug>.
  const isConvProduct = Boolean(isConv) && isConv !== 'false'
  const convSlug = slug || (isConvProduct && product.name ? slugifyCategory(product.name) : null)
  const canonical =
    isConvProduct && convSlug
      ? `${siteBase}/buy/${convSlug}`
      : `${siteBase}/product/${product.id}`

  // Category-as-product items are open-price ("you choose your amount"), so they carry a
  // min/max rather than a single price. An array makes buildProductJsonLd emit an
  // AggregateOffer; a price-less Offer would be invalid structured data.
  const minPrice = Number(product.minPrice)
  const maxPrice = Number(product.maxPrice)
  // Rounded: currency conversion leaves float noise (85.23570000000001) which looks
  // broken in structured data.
  const round2 = (n) => Math.round(n * 100) / 100
  const offerPrice =
    isConvProduct && Number.isFinite(minPrice) && Number.isFinite(maxPrice) && maxPrice > 0
      ? [round2(minPrice), round2(maxPrice)]
      : product.price

  const productJsonLd = buildProductJsonLd(
    {
      name: product.name,
      description: seoDescription,
      image: product.imageUrl,
      price: offerPrice,
      currency: product.currency || "ZAR",
      brand: seoBrand,
      category: product.category,
      url: canonical,
    },
    siteBase,
  )
  const breadcrumbJsonLd = buildBreadcrumbJsonLd(
    [
      { name: "Home", url: "/" },
      { name: "Products", url: "/products" },
      ...(product.category ? [{ name: product.category, url: `/products?category=${product.category_id}` }] : []),
      { name: product.name, url: canonical },
    ],
    siteBase,
  )

  return (
    <div className="product-detail-page">
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonical={canonical}
        ogImage={product.imageUrl}
        ogType="product"
        keywords={`${product.name}, buy ${product.name}, ${product.provider || ""} airtime, ${product.provider || ""} data bundles, Zimbabwe airtime, send airtime to Zimbabwe`}
        jsonLd={[productJsonLd, breadcrumbJsonLd]}
      />
      <div className="product-detail-container">
        <Breadcrumb className="breadcrumb">
          <Breadcrumb.Item>
            <Link to="/">Home</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/categories">Categories</Link>
          </Breadcrumb.Item>

            <Breadcrumb.Item>
              <Link to={`/products`}>
                 Products
              </Link>
            </Breadcrumb.Item>
          <Breadcrumb.Item>{product?.name}</Breadcrumb.Item>
        </Breadcrumb>

        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Row gutter={[32, 32]}>
            <Col xs={24} md={12}>
              <motion.div variants={itemVariants} className="product-image-wrapper">
                 <img 
                  width="100%" 
                  style={{ marginRight: "15px" }} 
                  alt={product.name} 
                  src={`../${product.imageUrl}`} 
                />
              </motion.div>
            </Col>

            <Col xs={24} md={12}>
              <motion.div variants={itemVariants}>
                <Title level={3}>{product.provider + ", " + product.name} {product.name.indexOf(product.currency) > -1 ? null : `, (${product.currency})`}</Title>
              </motion.div>

            {product?.product_notice?.length > 0 && (
              <>
                {product?.product_notice?.map((notice, index) => (
                  <motion.div 
                    key={index}
                    style={{ marginBottom: "20px" }}
                    variants={itemVariants}
                    className="product-info-box"
                    initial="hidden"
                    animate="visible"
                  >
                    <InfoCircleOutlined className="info-icon" />
                    <Text>{notice}</Text>
                  </motion.div>
                ))}
              </>
            )}

             {product?.category_notice?.length > 0 && (
                <>
                  {product.category_notice?.map((notice, index) => (
                    <motion.div 
                      key={index}
                      style={{ marginBottom: "20px" }}
                      variants={itemVariants}
                      className="product-info-box"
                      initial="hidden"
                      animate="visible"
                    >
                      <InfoCircleOutlined className="info-icon" />
                      <Text>{notice}</Text>
                    </motion.div>
                  ))}
                </>
             )}

             {product?.provider_notice?.length > 0 && (
                <>
                  {product.provider_notice?.map((notice, index) => (
                    <motion.div 
                      key={index}
                      style={{ marginBottom: "20px" }}
                      variants={itemVariants}
                      className="product-info-box"
                      initial="hidden"
                      animate="visible"
                    >
                      <InfoCircleOutlined className="info-icon" />
                      <Text>{notice}</Text>
                    </motion.div>
                  ))}
                </>
              )}

              { product.price &&
                <motion.div variants={itemVariants} className="product-price">
                  {formatPrice(product.price || 0)}
                </motion.div>
              }

              <motion.div variants={itemVariants}>
                <Paragraph className="product-short-description">{product?.description?.split("\n\n")[0]}</Paragraph>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Form form={form} layout="vertical" width='100%' onFinish={handleAddToCart} className="product-form">
                  {product.options?.map((option, index) => (
                    <Form.Item key={index} name={option.type} label={option.type} className="option-item">
                      <Select
                        defaultValue={option.values[0]}
                        onChange={(value) => handleOptionChange(option.type, value)}
                      >
                        {option.values.map((value, i) => (
                          <Option key={i} value={value}>
                            {value}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ))}

                  {
                    (isConv === "true" || isConv === true) && 
                      <motion.div
                        style={{marginBottom:"15px", maxWidth: "400px" }}
                        variants={itemVariants}
                        className="product-info-box"
                        initial="hidden"
                        animate="visible"
                      >
                        <InfoCircleOutlined className="info-icon" />
                        <Text>Receipient gets  <strong>{product.currency} {customPrice} </strong>   {product.name}</Text>
                      </motion.div>
                  }

                  { !product.price &&
                    <Form.Item name="userPrice" label="Amount (ZAR)" 
                      rules={[
                        {
                          required: true, 
                          message: "Amount is required" 
                        },
                        { 
                          type: 'number',
                          min: product?.minPrice,
                          message: `Amount cannot be less than ${Number(product?.minPrice)} ZAR`
                        },
                        { 
                          type: 'number',
                          max: Number(product.maxPrice), 
                          message: `Amount cannot exceed ${Number(product.maxPrice)} ZAR`
                        }
                      ]}
                    >
                        

                      <InputNumber onChange={(e)=> {
                         setCustomPrice((rate(e, product?.currency, product?.markupPercent))?.toFixed(2)
                         )}
                         } style={{width:"100%", maxWidth:"400px"}} min={Number(product.minPrice)} max={Number(product.maxPrice)} defaultValue={Number(product.price)} className="quantity-input" />
                    
                    </Form.Item>
                  }

                

                  <Form.Item label="Account / Phone Number" name="account" 
                    rules={[
                      { required: true, message:"Account / Phone Number is required" },
                      {
                        pattern: stringToRegex(product?.validation),//stringToRegex(product.validation),
                        message: product.validation_message
                      }
                      ]}>
                    <Input style={{width:"100%",maxWidth:"400px"}}/>
                  </Form.Item>

                {
                  product.required && product.required.length > 0 &&
                    product.required.map((req) => {
                      if(req.name === "ProductCode"){return null}
                      return (
                        <Form.Item 
                          key={req.name}
                          name={req.name}
                          label={req.description}
                          rules={[{ required: true, message: `${req.name} is required` }]}
                        >
                          <Input className="quantity-input" style={{ width: "100%", maxWidth: "400px" }} />
                        </Form.Item>
                      );
                    })
                }

                  <Form.Item className="form-actions">
                    <Space wrap style={{ width: '100%' }}>
                      <Button
                        type="default"
                        size="medium"
                        icon={<ShoppingCartOutlined />}
                        htmlType="submit"
                        className="add-to-cart-button"
                      >
                        Add to cart
                      </Button>
                      <Button
                        type="primary"
                        size="medium"
                        icon={<CreditCardFilled />}
                        onClick={handleBuyNow}
                        htmlType="button"
                        className="add-to-cart-button"
                      >
                        Buy Now
                      </Button>
                    </Space>

                
                  </Form.Item>
                </Form>
              </motion.div>

            </Col>
          </Row>

         {related.length > 0 &&
          <motion.div variants={itemVariants} className="related-products">
            <Title level={3}>Related products</Title>
            <Row gutter={[24, 24]}>
              {related.map((item) => (
                <Col xs={24} sm={12} md={6} key={item}>
                  <Card
                    hoverable
                    className="product-card"
                    cover={
                       <img 
                        width="100%" 
                        style={{ marginRight: "15px" }} 
                        alt={item.name} 
                        src={`../${item.icon}`}
                      />
                    }
                  >
                    {/* Same structure as the listing grids, and fixed for the same
                        reason: the link used to wrap the button as well, so the
                        clamp on .product-name clipped it to a sliver whenever the
                        list page's stylesheet was also loaded. */}
                    <Link
                      to={`/product/${item.id}?category=${item.category_id}`}
                      className="product-card-link"
                    >
                      <div className="product-card-title">{item.title}</div>
                      <div className="product-card-price">{formatPrice(item.price)}</div>
                    </Link>

                    <div className="product-card-action">
                      <Button
                        type="primary"
                        block
                        className="select-options-button"
                        onClick={() => navigate(`/product/${item.id}?category=${item.category_id}`)}
                      >
                        Buy Now
                      </Button>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </motion.div>
          }
        </motion.div>
      </div>
    </div>
  )
}

export default ProductDetailPage

